/* eslint-disable unicorn/prefer-single-call */
import { Paywall, TinypassSDK } from "@product/rosetta-sdk";
import { useSyncedRef, useUnmountEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { config } from "shared/data";
import { getLoginUrl } from "shared/lib/account";
import { notEmpty, parseDate } from "shared/lib/utils";

import { AppBarColorScheme, getAppBarVariant } from "scmp-app/components/app-bar/helpers";
import { useGetArticlePaywallTag } from "scmp-app/components/article/article-render/hooks/piano-integration/paywalls";
import { useShareContentUnlocked } from "scmp-app/components/article/article-render/hooks/share-content-unlocked";
import {
  checkCanShowInPlusPlatform,
  checkIsContainsPlusQueryString,
  checkIsPlusArticle,
} from "scmp-app/components/article/article-render/plus/helpers";
import { appAtom } from "scmp-app/lib/app/atoms";
import { useCurrentArticleIds } from "scmp-app/lib/current-item";
import { rosettaAtom, setPianoCustomVariable } from "scmp-app/lib/rosetta";
import { useRosettaPianoExecution } from "scmp-app/lib/rosetta/hooks/rosetta-piano-execution";
import type { pianoIntegrationArticle$key } from "scmp-app/queries/__generated__/pianoIntegrationArticle.graphql";

export const usePianoIntegration = (article_: pianoIntegrationArticle$key) => {
  const router = useRouter();
  const { currentArticleId } = useCurrentArticleIds();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const { isRosettaPianoExecutionReady, shouldSkipPianoExecution } = useRosettaPianoExecution(
    navigatorInfo,
    navigatorInfoLoaded,
  );

  const article = useFragment(
    graphql`
      fragment pianoIntegrationArticle on Article {
        __typename
        contentLock
        createdDate
        entityId
        entityUuid
        headline
        publishedDate
        sponsor
        sponsorType
        urlAlias
        flag
        authors {
          name
        }
        sectionsV2 {
          value {
            ... on Section {
              entityId
              entityUuid
              name
            }
          }
        }
        topics {
          entityId
          name
        }
        types {
          value {
            entityId
            name
          }
        }
        ...helpersAppBarVariantBase
        ...hooksPaywallPromiseContent
        ...helpersIsArchiveWallArticle
        ...helpersIsVelocityWallArticle
        ...helpersCheckIsPlusArticle
        ...helpersCheckCanShowInPlusPlatformContent
        ...shareContentUnlockedArticle
        ...paywallsGetArticlePaywallTagArticle
      }
    `,
    article_,
  );

  const latestRosettaInstance = useSyncedRef(asyncRosettaState?.result?.instance);

  const [isExecuted, setIsExecuted] = useState(false);
  const isShareContentUnlocked = useShareContentUnlocked(article);

  const { isAuthenticatedSignInWall, pianoArticlePaywallCategoryTag } =
    useGetArticlePaywallTag(article);

  useEffect(() => {
    const appSettings = asyncRosettaState?.result?.appSettings ?? {};

    /*
     * Prevent setting Piano Tags and Variables for already executed articles.
     *
     * Issue: Piano Experience Execute and ShowOffer operations are asynchronous.
     * If a user navigates back to a previous article before ShowOffer completes,
     * data inconsistencies can occur between execution and display.
     *
     * Solution: We prevent re-setting Piano data for executed articles and
     * block rendering of the next article until ShowOffer confirmation,
     * ensuring data integrity throughout the Piano experience flow.
     */
    if (
      isExecuted ||
      !notEmpty(appSettings) ||
      asyncRosettaState?.status !== "success" ||
      !isRosettaPianoExecutionReady ||
      currentArticleId !== article.entityId
    )
      return;

    const articleUrl = new URL(location.origin);
    articleUrl.pathname = article.urlAlias;

    const handleSetArticleVariables = () => {
      const subscribeUrl = new URL(config.rosetta.subscribeHost);
      subscribeUrl.searchParams.set("destination", articleUrl.toString());

      setPianoCustomVariable("entityUuid", article.entityUuid);
      setPianoCustomVariable("entityId", article.entityId);
      setPianoCustomVariable("headline", article.headline);
      setPianoCustomVariable("loginUrl", getLoginUrl(articleUrl.toString()));
      setPianoCustomVariable("subscribeUrl", subscribeUrl.toString());
      setPianoCustomVariable("theme", AppBarColorScheme[getAppBarVariant(article)]);
      setPianoCustomVariable(
        "topics",
        article.topics?.map(topic => topic?.name.replaceAll(/["#$%&'()*+.:<>?\\{}~]/g, "_")) ?? [],
      );
      setPianoCustomVariable(
        "sections",
        article?.sectionsV2?.map(section => section?.value?.map(value => value?.name).join("/")) ??
          [],
      );

      setPianoCustomVariable("articleContext", {
        entityId: article.entityId,
        flag: article?.flag,
        sections: first(article?.sectionsV2)?.value?.map(section => section) ?? [],
        topics: article?.topics?.map(t => t) ?? [],
      });
    };

    const handleSetGeneralVariables = () => {
      TinypassSDK.instance?.push(["setZone", "Web"]);
      TinypassSDK.instance?.push(["setPageURL", articleUrl.toString()]);

      const isScmpPlusArticle =
        checkIsPlusArticle(article) ||
        (checkCanShowInPlusPlatform(article) && checkIsContainsPlusQueryString(router.query));

      const contentUnlock =
        isShareContentUnlocked || !article?.contentLock || isAuthenticatedSignInWall
          ? "contentUnlocked"
          : "";
      const articleType = first(article.types?.flatMap(type => type?.value))?.name ?? "";

      TinypassSDK.instance?.push([
        "setTags",
        [
          // refs: https://project.scmp.tech/product/web/rosetta/-/issues/1084
          ...(article.topics?.map(topic =>
            topic?.name.replaceAll(/["#$%&'()*+.:<>?\\{}~]/g, "_"),
          ) ?? []),
          article.__typename ? `contentType/${article.__typename}` : "",
          contentUnlock,
          article.sponsorType ? `sponsorType/${article.sponsorType}` : "",
          pianoArticlePaywallCategoryTag,
          notEmpty(articleType) ? `articleType/${articleType}` : "",
          isScmpPlusArticle ? "scmp-plus" : "",
        ].filter(notEmpty),
      ]);
    };

    const handleSetContentVariables = () => {
      const firstSection = first(article.sectionsV2);
      TinypassSDK.instance?.push([
        "setContentCreated",
        parseDate(
          notEmpty(article.publishedDate) ? article.publishedDate : (article.createdDate ?? 0),
        ).toISOString(),
      ]);
      TinypassSDK.instance?.push(["setContentAuthor", first(article.authors)?.name ?? ""]);
      TinypassSDK.instance?.push(["setContentIsNative", notEmpty(article.sponsor)]);
      TinypassSDK.instance?.push([
        "setContentSection",
        (firstSection?.value?.map(section => section?.name).slice(-2) ?? []).join("/"),
      ]);
    };

    const handleSetNewsPassVariables = () => {
      setPianoCustomVariable(
        "isNewsPassTrialActive",
        asyncRosettaState?.result?.user?.newsPass?.isTrialActive,
      );
      setPianoCustomVariable(
        "hasUsedNewsPassOffer",
        asyncRosettaState?.result?.user?.newsPass?.hasUsedOffer,
      );
    };

    const handleSetChurnUserVariables = () => {
      setPianoCustomVariable("isSCMPChurned", asyncRosettaState?.result?.user?.isSCMPChurned);
    };

    handleSetArticleVariables();
    handleSetGeneralVariables();
    handleSetContentVariables();
    handleSetChurnUserVariables();
    handleSetNewsPassVariables();
    Paywall.closeActivePianoMeterWall();
    if (shouldSkipPianoExecution) return;
    latestRosettaInstance.current?.execute();
    setIsExecuted(true);
  }, [
    article,
    asyncRosettaState?.result?.appSettings,
    asyncRosettaState?.result?.user,
    asyncRosettaState?.status,
    latestRosettaInstance,
    currentArticleId,
    isExecuted,
    router,
    isShareContentUnlocked,
    shouldSkipPianoExecution,
    isRosettaPianoExecutionReady,
    isAuthenticatedSignInWall,
    pianoArticlePaywallCategoryTag,
  ]);

  useUnmountEffect(() => Paywall.closeActivePianoMeterWall());
};
